/**
 * Email Notifications Service (Mock v1)
 *
 * Simulates email notifications. Returns a notification object that
 * the caller can display via toast. Structured for easy integration
 * with a real provider later (SendGrid, Resend, etc.)
 *
 * Usage:
 *   import { notifySkillApproved } from '../services/emailNotifications';
 *   const notification = notifySkillApproved('Contract Drafting', 'creator@example.com');
 *   // notification => { type, title, message } — pass to toast
 */

/**
 * Build a mock email notification payload.
 * @param {string} type - 'success' | 'info' | 'warning' | 'error'
 * @param {string} subject - email subject line
 * @param {string} body - email body text
 * @param {string} recipient - target email
 * @returns {{ type: string, title: string, message: string, recipient: string }}
 */
function buildNotification(type, subject, body, recipient) {
  console.log(`[EMAIL MOCK] To: ${recipient} | Subject: ${subject}`);
  return {
    type,
    title: `Email: ${subject}`,
    message: `To: ${recipient} — ${body}`,
    recipient,
  };
}

/**
 * Notify creator that their skill was approved.
 * @param {string} skillName
 * @param {string} creatorEmail
 * @returns {{ type: string, title: string, message: string, recipient: string }}
 */
export function notifySkillApproved(skillName, creatorEmail) {
  return buildNotification(
    'success',
    `Skill "${skillName}" approved!`,
    `Your skill "${skillName}" has been approved and is now available on the marketplace.`,
    creatorEmail || 'creator@maxemployee.com'
  );
}

/**
 * Notify creator that their skill was rejected.
 * @param {string} skillName
 * @param {string} creatorEmail
 * @param {string} reason
 * @returns {{ type: string, title: string, message: string, recipient: string }}
 */
export function notifySkillRejected(skillName, creatorEmail, reason) {
  return buildNotification(
    'error',
    `Skill "${skillName}" rejected`,
    `Your skill "${skillName}" was not approved. Reason: ${reason || 'Not specified'}.`,
    creatorEmail || 'creator@maxemployee.com'
  );
}

/**
 * Notify about a new version of a skill.
 * @param {string} skillName
 * @param {string} version
 * @returns {{ type: string, title: string, message: string, recipient: string }}
 */
export function notifyNewVersion(skillName, version) {
  return buildNotification(
    'info',
    `New version of skill "${skillName}"`,
    `The skill "${skillName}" has been updated to version ${version}.`,
    'user@maxemployee.com'
  );
}

/**
 * Notify buyer of purchase confirmation.
 * @param {string} skillName
 * @param {string} buyerEmail
 * @returns {{ type: string, title: string, message: string, recipient: string }}
 */
export function notifyPurchaseConfirmation(skillName, buyerEmail) {
  return buildNotification(
    'success',
    `Purchase confirmed: "${skillName}"`,
    `Your purchase of skill "${skillName}" has been confirmed. Access the dashboard to start using it.`,
    buyerEmail || 'buyer@maxemployee.com'
  );
}
